import { z } from 'zod';

import { adminListQuerySchema, type AdminAuditAction, type AdminTargetType } from './admin.js';
import { listQuerySchema, type Page } from './common.js';
import { localeSchema, type Locale } from './locales.js';

/**
 * DESTEK BİLETİ — sözleşme (Faz 8).
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * Neden uygulama içi bilet, neden e-posta değil
 * ═══════════════════════════════════════════════════════════════════════════
 * Gerekçenin uzun hâli `schema.prisma`daki `SupportTicket` modelinin başında.
 * Kısası: SMTP bugün kapalı (Yandex 525) ve açıldığında da e-posta,
 * kullanıcının HANGİ çalışma alanından yazdığını, planını ve bağlantılarının
 * durumunu taşımıyor. Bilet kiracıya bağlı olduğu için personel ekranı bu
 * bağlamı sorgulayabiliyor.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 İKİ AYRI OKUYUCU KİTLESİ — tipler de ayrı
 * ═══════════════════════════════════════════════════════════════════════════
 * `SupportTicketDetail` (kullanıcı) ile `AdminSupportTicketDetail` (personel)
 * bilerek iki ayrı tip. Ortak bir tipe `internal?: boolean` eklemek daha kısa
 * olurdu ve tam olarak o kısalık tehlikeli: iç not sızıntısı bir alan
 * unutulunca değil, bir alanın VARLIĞI mümkün olduğu için doğar. Kullanıcı
 * tarafındaki tipte `internal` diye bir alan YOK — sızdırmak için önce tipi
 * değiştirmek gerekir ve o bir gözden geçirmede görülür.
 */

// ═══════════════════════════════════════════════════════════════════════════
// Prisma enum aynaları
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Prisma enum'larının ayna hâli — `enums.ts`teki desenin aynısı (frontend
 * Prisma client'ı import etmemeli).
 *
 * ⚠️ Aynalar normalde `enums.ts`te yaşıyor; bu üçü burada çünkü o dosya bu
 * turda başka bir yazarın alanında. Destek alanı barrel'a girdiğinde adlar
 * `enums.ts`e taşınabilir — davranış değişmez, yalnızca import yolu.
 */
export const SupportTicketStatus = {
  /// Yeni açıldı, personel henüz bakmadı.
  OPEN: 'OPEN',
  /// Personel yanıtladı, top kullanıcıda.
  WAITING_USER: 'WAITING_USER',
  /// Kullanıcı yanıtladı, top personelde.
  WAITING_STAFF: 'WAITING_STAFF',
  RESOLVED: 'RESOLVED',
  CLOSED: 'CLOSED',
} as const;
export type SupportTicketStatus = (typeof SupportTicketStatus)[keyof typeof SupportTicketStatus];

export const SupportTicketPriority = {
  LOW: 'LOW',
  NORMAL: 'NORMAL',
  HIGH: 'HIGH',
  /// Para ya da yayın duruyor.
  URGENT: 'URGENT',
} as const;
export type SupportTicketPriority =
  (typeof SupportTicketPriority)[keyof typeof SupportTicketPriority];

export const SupportAuthorRole = {
  USER: 'USER',
  STAFF: 'STAFF',
  /// Otomatik not: durum değişikliği, otomatik kapatma.
  SYSTEM: 'SYSTEM',
} as const;
export type SupportAuthorRole = (typeof SupportAuthorRole)[keyof typeof SupportAuthorRole];

export const SUPPORT_TICKET_STATUSES = [
  'OPEN',
  'WAITING_USER',
  'WAITING_STAFF',
  'RESOLVED',
  'CLOSED',
] as const;

export const SUPPORT_TICKET_PRIORITIES = ['LOW', 'NORMAL', 'HIGH', 'URGENT'] as const;

/**
 * Bilet kategorisi.
 *
 * Şemada `String`, enum DEĞİL — gerekçe modelin başında: kategori listesi ürün
 * büyüdükçe değişiyor ve her değişiklik migration istemesin. Doğrulama burada,
 * Zod tarafında; yani liste bir kod dağıtımıyla büyüyor.
 */
export const SUPPORT_CATEGORIES = [
  /// Fatura, plan, kredi, ödeme.
  'billing',
  /// Ürün hatası, ekran açılmıyor, sayı yanlış.
  'technical',
  /// Meta/Google bağlantısı, senkron, hesap eşleme.
  'integration',
  /// Özellik isteği.
  'feature',
  'other',
] as const;
export type SupportCategory = (typeof SUPPORT_CATEGORIES)[number];

// ═══════════════════════════════════════════════════════════════════════════
// Sınırlar
// ═══════════════════════════════════════════════════════════════════════════

/**
 * 🔴 AÇIK BİLET TAVANI — kuyruğu KİRACILAR ARASI paylaşıyoruz.
 *
 * Personel kuyruğu tek ve tüm kiracılardan besleniyor. Döngüye giren bir
 * istemci (ya da sinirli bir kullanıcı) yüz bilet açtığında zarar kendi
 * çalışma alanıyla sınırlı kalmıyor — kuyruğun başındaki BAŞKA bir kiracının
 * gerçek sorunu o yüz satırın altında kayboluyor. Hız sınırı bunu tek başına
 * çözmüyor: dakikada üç bilet de bir günde 4.000 bilet eder.
 *
 * Sayı 10: bir çalışma alanının aynı anda gerçekten bekleyen on ayrı sorunu
 * varsa cevap yeni bir bilet değil, var olanlardan birine yazmak.
 */
export const MAX_OPEN_TICKETS_PER_WORKSPACE = 10;

/// Mesaj gövdesi üst sınırı. Log yapıştıran kullanıcı için 8.000 karakter
/// yeterli, 100 KB'lık bir yığın ise okunmaz ve tabloyu şişirir. Gerçekten
/// büyük çıktılar EK DOSYA olarak gidiyor (`SUPPORT_ATTACHMENT_LIMITS`).
export const SUPPORT_MESSAGE_MAX_LENGTH = 8_000;

// ═══════════════════════════════════════════════════════════════════════════
// EK DOSYA
// ═══════════════════════════════════════════════════════════════════════════

/**
 * 🔴 DOSYA API SÜRECİNDEN GEÇMİYOR.
 *
 * Akış üç adım: istemci `POST …/uploads` ile İMZALI bir PUT adresi alıyor,
 * baytları doğrudan R2'ye yazıyor, sonra dönen ANAHTARI mesajla birlikte
 * gönderiyor. Multipart gövde ile yüklemek daha az adım olurdu ve üç şeyi
 * bozardı: 25 MB'lık bir video API sürecinin belleğine girerdi, Fastify'ın
 * gövde sınırı tüm uçlar için yükseltilmek zorunda kalırdı, ve yükleme
 * süresince bir HTTP işçisi bloke olurdu.
 *
 * Aynı desen kreatif yüklemesinde de var (`creative.upload.ts`); oradaki
 * gerekçeler burada da geçerli ve TEKRARLANMADI.
 *
 * ---------------------------------------------------------------------------
 * 🔴 SVG ve HTML allowlist'te YOK — ve olmayacak
 * ---------------------------------------------------------------------------
 * İkisi de belge biçimi: içine `<script>` gömülebiliyor. İmzalı URL bizim
 * alan adımızda servis edildiğinde o script kullanıcının oturum çerezine
 * erişir. "Ek dosya"nın kimlik hırsızlığına dönüşmesi tam olarak bu yolla
 * oluyor. Liste BEYAZ liste — `image/svg` gibi bir varyant kara listeyi
 * atlatırdı.
 *
 * ⚠️ Destek eki kreatif yüklemesinden DAHA tehlikeli: dosyayı YABANCI
 * biri (kiracı) yüklüyor ve onu PERSONEL açıyor. Kreatifte yükleyen ile
 * açan aynı kişi.
 */
export const SUPPORT_ATTACHMENT_IMAGE_MIMES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
] as const;

/// Belge ve düz metin. `application/zip` BİLEREK yok: içeriği taranamıyor ve
/// personel onu açmak zorunda kalıyor. Log dosyası `text/plain` olarak gelir.
export const SUPPORT_ATTACHMENT_DOCUMENT_MIMES = [
  'application/pdf',
  'text/plain',
  'text/csv',
  'application/json',
] as const;

/// Ekran kaydı — destek biletinin en açıklayıcı eki ve ayrı bir boyut sınıfı.
export const SUPPORT_ATTACHMENT_VIDEO_MIMES = ['video/mp4'] as const;

export const SUPPORT_ATTACHMENT_MIMES = [
  ...SUPPORT_ATTACHMENT_IMAGE_MIMES,
  ...SUPPORT_ATTACHMENT_DOCUMENT_MIMES,
  ...SUPPORT_ATTACHMENT_VIDEO_MIMES,
] as const;
export type SupportAttachmentMime = (typeof SUPPORT_ATTACHMENT_MIMES)[number];

/**
 * Boyut tavanları TÜRE GÖRE ayrı.
 *
 * Tek bir tavan seçilseydi ikisinden biri yanlış olurdu: videoya yetecek bir
 * sayı (25 MB) bir PNG için fazlasıyla geniş ve depolama maliyetini kiracı
 * başına ölçülemez yapardı; PNG'ye yetecek bir sayı (5 MB) ise ekran kaydını
 * imkânsız kılardı — ve ekran kaydı, destek biletinde metinden daha çok iş
 * görüyor.
 */
export const SUPPORT_ATTACHMENT_LIMITS = {
  imageMaxBytes: 10 * 1024 * 1024,
  documentMaxBytes: 10 * 1024 * 1024,
  videoMaxBytes: 25 * 1024 * 1024,
  /// TEK MESAJDAKİ en fazla ek sayısı. Bilet başına değil mesaj başına:
  /// uzun bir yazışmada her turda ekran görüntüsü paylaşmak meşru.
  maxPerMessage: 5,
} as const;

export function supportAttachmentMaxBytes(mime: string): number {
  if ((SUPPORT_ATTACHMENT_VIDEO_MIMES as readonly string[]).includes(mime)) {
    return SUPPORT_ATTACHMENT_LIMITS.videoMaxBytes;
  }
  if ((SUPPORT_ATTACHMENT_IMAGE_MIMES as readonly string[]).includes(mime)) {
    return SUPPORT_ATTACHMENT_LIMITS.imageMaxBytes;
  }
  return SUPPORT_ATTACHMENT_LIMITS.documentMaxBytes;
}

/**
 * İmzalı yükleme adresi isteği.
 *
 * `sizeBytes` İSTEMCİDEN geliyor ve bir taahhüt: imzalı PUT `Content-Length`i
 * bu değere KİLİTLİYOR, yani yalan söyleyen istemcinin yüklemesi R2 tarafında
 * reddediliyor. Sunucuya güvendirmiyoruz — imza güvendiriyor.
 */
export const supportUploadRequestSchema = z.object({
  fileName: z.string().trim().min(1).max(200),
  mimeType: z.enum(SUPPORT_ATTACHMENT_MIMES),
  sizeBytes: z.number().int().positive(),
});
export type SupportUploadRequestInput = z.infer<typeof supportUploadRequestSchema>;

export interface SupportUploadTicket {
  /// İmzalı PUT adresi. İstemci baytları BURAYA yazıyor.
  url: string;
  /// Mesajla birlikte geri gönderilecek anahtar. Sunucu bunu doğruluyor:
  /// önekin kiracıya ait olması ŞART (`isOwnSupportKey`).
  key: string;
  expiresAt: string;
  /// İstemcinin PUT'ta AYNEN göndermesi gereken başlıklar. İmza bunları
  /// kapsıyor; eksik gönderilirse R2 sessiz 403 veriyor ve hata istemcide
  /// "yükleme başarısız" olarak görünüyor — sebebi görünmeden.
  requiredHeaders: Record<string, string>;
}

/**
 * Mesaja iliştirilen anahtarlar.
 *
 * Varsayılan boş dizi: istemcinin alanı hiç göndermemesi meşru ve `undefined`
 * ile `[]` arasındaki farkı servise taşımanın bir faydası yok.
 */
export const supportAttachmentKeysSchema = z
  .array(z.string().min(1).max(512))
  .max(SUPPORT_ATTACHMENT_LIMITS.maxPerMessage)
  .default([]);

/// Saklanan ek dosya tanımı — `support_ticket_messages.attachmentsEnc`
/// içinde ŞİFRELİ bir JSON dizisi olarak duruyor. Dosya ADI da şifreli:
/// "fatura-ahmet-yilmaz.pdf" gövdedeki metin kadar kişisel veri.
export interface SupportAttachment {
  key: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
}

/// Sözleşmeye çıkan hâli — imzalı indirme adresiyle.
export interface SupportAttachmentView extends SupportAttachment {
  /// Kısa ömürlü imzalı indirme adresi. Depolama yapılandırılmamışsa `null`
  /// (satır yine görünür — gerekçe `SupportMessageRow.attachments` üzerinde).
  url: string | null;
}

// ═══════════════════════════════════════════════════════════════════════════
// SLA
// ═══════════════════════════════════════════════════════════════════════════

/**
 * İLK YANIT hedefi — dakika, önceliğe göre.
 *
 * 🔴 Ölçülen şey ÇÖZÜM değil İLK YANIT ve bu bilinçli. Çözüm süresi büyük
 * ölçüde sorunun kendisine bağlı (bir Meta kesintisini destek ekibi
 * çözemiyor); ilk yanıt ise tamamen bizim elimizde ve kullanıcının
 * "duyulduğunu" hissettiği an tam olarak o. Çözüm süresini hedefe bağlamak,
 * ekibi biletleri erken kapatmaya iter — ölçtüğün şeyi bozarsın.
 *
 * Sayılar takvim dakikası, iş saati DEĞİL. Mesai takvimi (tatil, hafta sonu,
 * saat dilimi) bugün yok ve onu uydurmak, hedefi ölçülemez yapardı. Gerçek
 * bir mesai takvimi geldiğinde değişecek tek yer bu tablo ve
 * `supportSlaDueAt`.
 */
export const SUPPORT_SLA_MINUTES: Record<SupportTicketPriority, number> = {
  URGENT: 60,
  HIGH: 4 * 60,
  NORMAL: 24 * 60,
  LOW: 72 * 60,
};

/// Bilet açılışı/öncelik değişimi → ilk yanıt son tarihi.
export function supportSlaDueAt(priority: SupportTicketPriority, from: Date): Date {
  return new Date(from.getTime() + SUPPORT_SLA_MINUTES[priority] * 60_000);
}

/**
 * `RESOLVED` bilet kaç gün sonra kendiliğinden kapanır.
 *
 * Kapanmasaydı `RESOLVED` biletler sonsuza kadar birikirdi ve hiçbiri
 * kullanıcının onayını taşımazdı — "çözüldü" diyen personel, kullanıcı ise
 * hiç geri dönmemiş. Yedi gün, kullanıcının "hâlâ olmuyor" demesi için
 * fazlasıyla yeterli ve bir tatil haftasını kapsıyor.
 *
 * ⚠️ Otomatik kapanış bir SYSTEM satırı yazıyor ve kullanıcıya bildiriliyor;
 * sessiz kapanış, kullanıcının yazdığı bir yanıtın karşılıksız kalması gibi
 * görünürdü.
 */
export const SUPPORT_AUTO_CLOSE_DAYS = 7;

export function supportAutoCloseAt(from: Date): Date {
  return new Date(from.getTime() + SUPPORT_AUTO_CLOSE_DAYS * 24 * 60 * 60 * 1000);
}

// ═══════════════════════════════════════════════════════════════════════════
// MEMNUNİYET PUANI
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Puanlama TEK SEFERLİK ve yalnız ÇÖZÜLMÜŞ/KAPANMIŞ bilette.
 *
 * Açık bilette puan istemek, sorunu hâlâ süren kullanıcıdan hüküm istemek
 * demek — gelen sayı destek kalitesini değil kullanıcının o anki sinirini
 * ölçer. Değiştirilebilir olsaydı da aynı sorun: personel "puanınızı
 * düzeltir misiniz" diyebilirdi ve ölçüm ekibin kendisine bağlanırdı.
 */
export const supportRatingSchema = z.object({
  rating: z.number().int().min(1).max(5),
  /// Serbest metin — şifreli saklanıyor, gövdelerle aynı kasada.
  comment: z.string().trim().max(1_000).optional(),
});
export type SupportRatingInput = z.infer<typeof supportRatingSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// Kullanıcı tarafı — girdi
// ═══════════════════════════════════════════════════════════════════════════

/**
 * 🔴 KULLANICI `URGENT` SEÇEMEZ.
 *
 * Personel kuyruğu `priority` ile sıralanıyor. Kullanıcıya "acil" düğmesi
 * verildiğinde herkes onu seçer — bu bir tahmin değil, her destek ürününde
 * yaşanan şey — ve sıralama bir şey ifade etmeyi bırakır. O noktada kuyruk
 * yalnızca tarihe göre sıralı bir listeye döner ve gerçekten para duran bilet
 * sıradan bir soru ile aynı yerde bekler.
 *
 * `URGENT` yalnız personelin `PATCH /admin/support/tickets/:id` ile
 * verebildiği bir işaret. Kullanıcının aciliyeti `HIGH` ile ifade ediliyor ve
 * kategorisi (`billing`) zaten ikinci bir sinyal.
 */
export const supportUserPrioritySchema = z.enum(['LOW', 'NORMAL', 'HIGH']).default('NORMAL');

export const supportTicketCreateSchema = z.object({
  subject: z.string().trim().min(4).max(160),
  category: z.enum(SUPPORT_CATEGORIES),
  /**
   * İlk mesaj. Bilet ile mesajı AYRI iki istekte almıyoruz: gövdesiz bir
   * bilet personel kuyruğunda "konu var, sorun yok" satırı olarak görünürdü
   * ve ilk mesaj hiç yazılmadan sekme kapanırsa kalıcı olarak öyle kalırdı.
   *
   * 🔴 Alan adı `body` — bir dönem `message` idi. Yanıt yazma uçları
   * (`supportMessageCreateSchema`, `adminSupportMessageCreateSchema`) ve
   * dönen mesaj nesnesi hep `body` diyordu; tek kavramın iki adı olması,
   * istemcinin hangi uçta hangi adı kullanacağını EZBERLEMESİ demekti.
   * Alt sınır burada 10 karakter (yanıtlarda 1): ilk mesaj sorunu anlatmak
   * zorunda, "merhaba" diyen bir bilet personel için kayıp bir tur.
   */
  body: z.string().trim().min(10).max(SUPPORT_MESSAGE_MAX_LENGTH),
  priority: supportUserPrioritySchema,
  attachmentKeys: supportAttachmentKeysSchema,
});
export type SupportTicketCreateInput = z.infer<typeof supportTicketCreateSchema>;

export const supportMessageCreateSchema = z.object({
  /**
   * Gövde alt sınırı 1 — ama ek dosya varsa 0 da kabul (`.superRefine`
   * aşağıda değil, serviste değil, TAM BURADA olmalıydı ve olmadı: Zod'un
   * `min(1)`i ek dosyayı görmüyor).
   *
   * Çözüm alt sınırı 0'a indirip "gövde YA DA ek zorunlu" kuralını şemanın
   * kendisine yazmak. Alternatif — boş gövdeyi serviste reddetmek — aynı
   * kuralı iki yere bölerdi ve uçların biri (ajan, otomasyon) pipe'sız
   * çağrıldığında kural düşerdi.
   */
  body: z.string().trim().max(SUPPORT_MESSAGE_MAX_LENGTH),
  attachmentKeys: supportAttachmentKeysSchema,
});
export type SupportMessageCreateInput = z.infer<typeof supportMessageCreateSchema>;

/**
 * Liste filtresi.
 *
 * `active` gerçek bir durum DEĞİL, üç durumun kısayolu
 * (`OPEN` + `WAITING_USER` + `WAITING_STAFF`). Listenin varsayılan sekmesi
 * "açık olanlar" ve o sekme üç durumu birden kapsıyor; kısayol olmasaydı
 * arayüz ya üç ayrı istek atardı ya da sunucu varsayılanda kapanmış biletleri
 * de döndürürdü.
 */
export const supportTicketStatusFilterSchema = z.enum([...SUPPORT_TICKET_STATUSES, 'active']);
export type SupportTicketStatusFilter = z.infer<typeof supportTicketStatusFilterSchema>;

export const supportTicketListQuerySchema = listQuerySchema.extend({
  status: supportTicketStatusFilterSchema.optional(),
  category: z.enum(SUPPORT_CATEGORIES).optional(),
});
export type SupportTicketListQuery = z.infer<typeof supportTicketListQuerySchema>;

// ═══════════════════════════════════════════════════════════════════════════
// Kullanıcı tarafı — çıktı
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Mesaj — KULLANICI görünümü.
 *
 * `internal` alanı YOK ve bu tipin en önemli özelliği bu (dosya başındaki
 * nota bak). İç notlar sunucuda filtreleniyor; tip de onları taşıyamıyor.
 */
export interface SupportMessageRow {
  id: string;
  authorRole: SupportAuthorRole;
  /// Yazarın görünen adı. Personel mesajlarında gerçek ad DEĞİL, sabit bir
  /// takım adı dönüyor — gerekçe `SUPPORT_STAFF_DISPLAY_NAME` üzerinde.
  authorName: string | null;
  /**
   * Mesaj metni — ÇÖZÜLMÜŞ hâli.
   *
   * Veritabanında şifreli duruyor (`v1:<iv>:<tag>:<ciphertext>`); sözleşmede
   * düz metin. Şifreli hâlin sözleşmeye sızmaması bilinçli: istemciye
   * çözemeyeceği bir dize göndermek, alanı "bazen metin bazen çöp" yapardı.
   * Gerekçenin uzunu `support-crypto.service.ts` başında.
   */
  body: string;
  /**
   * Gövde ÇÖZÜLEMEDİ mi.
   *
   * `true` olduğunda `body` boş dize ve arayüz "bu mesaj görüntülenemiyor"
   * basıyor. Alternatif — tüm isteği 500 ile düşürmek — tek bozuk satır
   * yüzünden on satırlık bir yazışmanın tamamını görünmez yapardı.
   *
   * Neredeyse her zaman `false`: dolduğu tek senaryo kasa anahtarının
   * değişmesi. Bayrak yine de sözleşmede çünkü o senaryoda arayüzün BOŞ bir
   * baloncuk basması, mesajın hiç yazılmadığı anlamına gelirdi.
   */
  unreadable: boolean;
  /**
   * Ek dosyalar — imzalı önizleme/indirme adresleriyle.
   *
   * Boş dizi, `undefined` DEĞİL: istemci `?.length` yazmak zorunda kalmasın.
   * Depolama yapılandırılmamışsa `url` alanı `null` gelir; satır yine
   * görünür (dosyanın VARLIĞI bilgi taşıyor, indirilememesi ayrı bir sorun).
   */
  attachments: SupportAttachmentView[];
  createdAt: string;
}

export interface SupportTicketRow {
  id: string;
  subject: string;
  category: SupportCategory;
  status: SupportTicketStatus;
  priority: SupportTicketPriority;
  /// Liste sıralamasının kaynağı. `updatedAt` DEĞİL — durum değişikliği de
  /// onu tazeliyor ve "en son yazılan bilet" sıralaması bozuluyordu.
  lastMessageAt: string;
  /// Kullanıcı için okunmamış yanıt var mı (`userUnread`). Rozetin kaynağı.
  unread: boolean;
  messageCount: number;
  createdAt: string;
}

export interface SupportTicketDetail extends SupportTicketRow {
  messages: SupportMessageRow[];
  /// Kapanmış bilete yazılamaz; arayüz yazma kutusunu buna bakarak kapatıyor
  /// (durum listesini tekrar yorumlamak zorunda kalmasın).
  canReply: boolean;
  /**
   * Memnuniyet puanı verilebilir mi.
   *
   * Sunucuda hesaplanıyor (`RESOLVED`/`CLOSED` + henüz puanlanmamış), çünkü
   * kural iki alana birden bakıyor ve arayüzün onu yeniden kurması, kuralın
   * değiştiği gün iki yerin ayrışması demekti — `canReply` ile aynı gerekçe.
   */
  canRate: boolean;
  /// Verilmiş puan (1–5) ya da `null`. Puanlama TEK SEFERLİK.
  rating: number | null;
  /// Puanla birlikte yazılan serbest metin. Şifreli saklanıyor, burada çözük.
  ratingComment: string | null;
}

/**
 * Liste yanıtı.
 *
 * `unreadCount` sayfalamadan BAĞIMSIZ: sol menüdeki rozet "bu sayfada kaç
 * okunmamış var" değil, "toplam kaç" demek. Sayfa içinden hesaplanamaz.
 */
export interface SupportTicketListResponse extends Page<SupportTicketRow> {
  unreadCount: number;
  /// Bu çalışma alanında yeni bilet açılabilir mi
  /// (`MAX_OPEN_TICKETS_PER_WORKSPACE`). Arayüz düğmeyi önceden kapatabilsin
  /// diye — 409'u kullanıcıya göstermek yerine.
  canOpenNew: boolean;
}

/**
 * Personel mesajlarında görünen ad.
 *
 * Gerçek ad DÖNMÜYOR ve bu bir gizlilik kararı: destek yanıtı veren kişi
 * platform çalışanı; adı ve e-postası kiracıya açılırsa geri alınamaz.
 * Kullanıcının ihtiyacı "bu satırı bir insan mı yazdı" bilgisi, kim olduğu
 * değil. Arayüz bu dizeyi çeviri anahtarı olarak da kullanabilir.
 */
export const SUPPORT_STAFF_DISPLAY_NAME = 'Mentorivo Support';

// ═══════════════════════════════════════════════════════════════════════════
// Personel tarafı
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Personel kuyruğu filtresi.
 *
 * ⚠️ `q` çalışma alanı adında DEĞİL, bilet KONUSUNDA ve mesaj gövdesinde
 * arıyor. Çalışma alanı için ayrı bir `workspaceId` filtresi var: "şu kiracının
 * tüm biletleri" ile "şu kelimeyi içeren biletler" iki farklı soru ve tek
 * kutuya sıkıştırıldığında ikisi de yanlış cevaplanıyor.
 */
export const adminSupportTicketListQuerySchema = adminListQuerySchema.extend({
  status: supportTicketStatusFilterSchema.optional(),
  priority: z.enum(SUPPORT_TICKET_PRIORITIES).optional(),
  category: z.enum(SUPPORT_CATEGORIES).optional(),
  workspaceId: z.uuid().optional(),
  /**
   * Atama filtresi — üç biçim.
   *
   *   `me`          → isteği yapan personelin üzerindekiler
   *   `unassigned`  → sahipsiz kuyruk (vardiya başında bakılan liste)
   *   `<uuid>`      → belirli bir personelin kuyruğu
   *
   * `me` sunucuda çözülüyor, istemcide DEĞİL: istemci kendi kullanıcı
   * kimliğini biliyor ama onu sorguya koymak, başka birinin kimliğini yazıp
   * o kişinin kuyruğunu okumakla aynı ucu paylaşmak demekti. Personel
   * arasında böyle bir gizlilik sınırı yok (hepsi `PlatformAdminGuard`ın
   * arkasında), ama "benim kuyruğum" düğmesinin YANLIŞ kimlik göndermesi
   * sessiz bir hata olurdu.
   */
  assignee: z.union([z.literal('me'), z.literal('unassigned'), z.uuid()]).optional(),
  /// Yalnız SLA'sı aşılmış biletler. Kuyruğun üstündeki kırmızı şeridin
  /// kaynağı; `slaBreachedAt` dolu olanlar.
  /**
   * ⚠️ `.optional()` EN SONDA ve bu bir tip meselesi, üslup değil.
   *
   * `.optional().transform(…)` yazılsaydı çıktı tipi yine `boolean |
   * undefined` olurdu ama ANAHTAR ZORUNLU kalırdı: `z.object` bir alanı
   * ancak şemanın kendisi `undefined` kabul ediyorsa isteğe bağlı sayıyor
   * ve `transform` bu bilgiyi yutuyor. Sonuç, sorguyu elle kuran her
   * çağıranın (testler, dâhilî çağrılar) `breached: undefined` yazmak
   * zorunda kalması — derlenmeyen ve sebebi görünmeyen bir hata.
   */
  breached: z
    .union([z.boolean(), z.enum(['true', 'false'])])
    .transform((v) => v === true || v === 'true')
    .optional(),
});
export type AdminSupportTicketListQuery = z.infer<typeof adminSupportTicketListQuerySchema>;

export const adminSupportMessageCreateSchema = z.object({
  body: z.string().trim().min(1).max(SUPPORT_MESSAGE_MAX_LENGTH),
  /**
   * İç not — kullanıcıya GÖRÜNMEZ.
   *
   * Varsayılan `false` bilinçli ve tersi tehlikeli olurdu: varsayılan `true`
   * olsaydı, bayrağı göndermeyi unutan bir istemci kullanıcının beklediği
   * yanıtı sessizce iç nota çevirirdi — kullanıcı cevapsız kalır, personel
   * "yazdım" sanırdı. Yanlış yöne düşen hata, notun görünmesi değil,
   * yanıtın görünmemesi.
   */
  internal: z.boolean().default(false),
  /**
   * Yanıtla birlikte durumu değiştir. Ayrı bir `PATCH` atmaya gerek kalmasın
   * diye: "cevapladım ve çözüldü" tek bir eylem ve iki istek arasında pencere
   * bırakmak, ikincisi düşerse biletin yanlış durumda kalması demek.
   */
  status: z.enum(SUPPORT_TICKET_STATUSES).optional(),
  /// Personel de ek gönderebiliyor (ekran görüntüsü, düzeltilmiş dosya).
  /// Anahtarlar `POST /admin/support/tickets/:id/uploads` ile alınıyor.
  attachmentKeys: supportAttachmentKeysSchema,
});
export type AdminSupportMessageCreateInput = z.infer<typeof adminSupportMessageCreateSchema>;

/// Durum/öncelik değişikliği. İkisinden en az biri zorunlu — boş bir PATCH
/// denetim kaydına "hiçbir şey değişmedi" satırı yazardı.
export const adminSupportTicketPatchSchema = z
  .object({
    status: z.enum(SUPPORT_TICKET_STATUSES).optional(),
    priority: z.enum(SUPPORT_TICKET_PRIORITIES).optional(),
  })
  .refine((v) => v.status !== undefined || v.priority !== undefined, {
    message: 'En az bir alan gerekli: status ya da priority',
    path: ['status'],
  });
export type AdminSupportTicketPatchInput = z.infer<typeof adminSupportTicketPatchSchema>;

/// Mesaj — PERSONEL görünümü. `internal` yalnız burada var.
export interface AdminSupportMessageRow extends SupportMessageRow {
  internal: boolean;
  authorUserId: string | null;
  /// Kullanıcı mesajlarında yazarın e-postası; personel/sistem satırlarında
  /// `null`. Personel ekranı "kim yazdı"yı bilmek zorunda.
  authorEmail: string | null;
}

export interface AdminSupportTicketRow {
  id: string;
  subject: string;
  category: SupportCategory;
  status: SupportTicketStatus;
  priority: SupportTicketPriority;
  lastMessageAt: string;
  createdAt: string;
  /// Personel için okunmamış (`staffUnread`).
  unread: boolean;
  messageCount: number;
  workspaceId: string;
  workspaceName: string | null;
  createdByEmail: string | null;
  /// Biletin açılışından bu yana geçen saat. Sunucuda hesaplanıyor ki liste
  /// SSR ile CSR arasında farklı bir sayı göstermesin (`ResponseMeta`
  /// başlığındaki `generatedAt` gerekçesinin aynısı).
  ageHours: number;

  // ── Atama ───────────────────────────────────────────────────────────────
  /// Bileti üstlenen personel. `null` = sahipsiz kuyrukta.
  assigneeUserId: string | null;
  /// Personelin ADI — kiracıya ASLA dönmeyen bilgi. Burada dönüyor çünkü bu
  /// tip yalnız `/admin` uçlarından çıkıyor (`SUPPORT_STAFF_DISPLAY_NAME`
  /// gerekçesinin sınırı: gizlenen şey kiracıdan, personelden değil).
  assigneeName: string | null;

  // ── SLA ─────────────────────────────────────────────────────────────────
  /// İlk yanıtın verilmesi gereken an. Bilet açılırken önceliğe göre
  /// hesaplanıyor; öncelik değişince YENİDEN hesaplanıyor.
  slaDueAt: string | null;
  /// İlk yanıt gerçekten ne zaman verildi. `null` + geçmiş `slaDueAt` = gecikme.
  firstResponseAt: string | null;
  /// Gecikmenin İŞARETLENDİĞİ an (worker yazıyor). `null` = ihlal yok.
  /// `slaDueAt < now` ile aynı şey değil: bu alan, uyarının GÖNDERİLDİĞİNİ de
  /// söylüyor ve worker'ın aynı bileti ikinci kez bildirmesini engelliyor.
  slaBreachedAt: string | null;

  // ── Kalite ──────────────────────────────────────────────────────────────
  /// Kullanıcının verdiği memnuniyet puanı (1–5) ya da `null`.
  rating: number | null;
  /// Bilet kaç kez yeniden açıldı (`RESOLVED` → kullanıcı yazdı). Yüksek sayı
  /// "çözüldü" denip çözülmemiş bir sorun demek; kuyruk raporunun sinyali.
  reopenCount: number;
}

export interface AdminSupportTicketDetail extends AdminSupportTicketRow {
  messages: AdminSupportMessageRow[];
  /// Kiracının bağlamı — personelin ilk bakışta görmesi gereken şey. Biletin
  /// e-postaya karşı asıl üstünlüğü bu (dosya başındaki nota bak).
  context: {
    plan: string | null;
    subscriptionStatus: string | null;
    memberCount: number;
    connectionCount: number;
    /// Kopuk ya da bozuk bağlantı sayısı — `integration` kategorili biletlerin
    /// yarısının cevabı burada.
    brokenConnectionCount: number;
    /**
     * Bu kiracının DAHA ÖNCE açtığı bilet sayısı (bu bilet hariç).
     *
     * "İlk kez mi yazıyor, yoksa altıncı kez aynı şeyi mi soruyor" sorusu,
     * yanıtın tonunu ve aciliyetini değiştiriyor. Sayı `RESOLVED`/`CLOSED`
     * dâhil hepsini kapsıyor: geçmişin tamamı.
     */
    previousTicketCount: number;
  };
  /// Puanla birlikte yazılan serbest metin — personel görünümünde de var.
  /// Kullanıcı tarafındaki `ratingComment` ile aynı satır, aynı çözme yolu.
  ratingComment: string | null;
}

// ═══════════════════════════════════════════════════════════════════════════
// Personel — atama
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Bileti bir personele bağla ya da bırak.
 *
 * `null` = sahipsiz kuyruğa geri koy. Ayrı bir `DELETE` ucu yazılmadı: atama
 * bir KAYNAK değil bir ALAN ve "sil" ile "boşalt" aynı şey. Tek uç, iki
 * yönde de aynı denetim satırını yazıyor (`before`/`after` farkı okunuyor).
 *
 * ⚠️ Hedefin `isPlatformAdmin` olması ŞART ve serviste doğrulanıyor. Kiracı
 * kullanıcısına atanmış bir bilet, o kişiye hiçbir şey göstermezdi
 * (personel ekranı `PlatformAdminGuard` arkasında) ama kuyrukta "atanmış"
 * görünüp kimsenin bakmadığı bir satır üretirdi — sessiz kayıp.
 */
export const adminSupportAssignSchema = z.object({
  assigneeUserId: z.uuid().nullable(),
});
export type AdminSupportAssignInput = z.infer<typeof adminSupportAssignSchema>;

/// Atanabilir personel — `GET /admin/support/staff`. Kuyruk ekranındaki açılır
/// listenin kaynağı; üzerindeki açık bilet sayısıyla birlikte geliyor ki
/// atama yapan kişi kimin dolu olduğunu görsün.
export interface AdminSupportStaffRow {
  userId: string;
  name: string | null;
  email: string;
  /// Bu kişinin üzerindeki KAPANMAMIŞ bilet sayısı.
  openCount: number;
}

// ═══════════════════════════════════════════════════════════════════════════
// Personel — kuyruk sağlığı
// ═══════════════════════════════════════════════════════════════════════════

/**
 * `GET /admin/support/stats` — kuyruğun tek bakışta hâli.
 *
 * Liste ucundan TÜRETİLEMEZ: sayılar sayfalamadan bağımsız ve ortalamalar
 * kapanmış biletleri de kapsıyor (liste varsayılanda kapsamıyor). İstemcinin
 * bunu hesaplaması, tüm biletleri çekmesi demekti.
 *
 * Ortalamalar DAKİKA ve `null` olabiliyor: hiç yanıtlanmamış bir kuyrukta
 * "0 dakika" yazmak, mükemmel bir performans gibi okunurdu.
 */
export interface AdminSupportStats {
  /// Kapanmamış bilet — `OPEN` + `WAITING_USER` + `WAITING_STAFF`.
  activeCount: number;
  /// Personelin aksiyonunu bekleyenler — `OPEN` + `WAITING_STAFF`.
  awaitingStaffCount: number;
  unassignedCount: number;
  /// SLA'sı aşılmış ve hâlâ ilk yanıtı verilmemiş biletler.
  breachedCount: number;
  byPriority: Record<SupportTicketPriority, number>;
  byStatus: Record<SupportTicketStatus, number>;
  /// İlk yanıta kadar geçen ortalama süre (dakika) — son 30 gün.
  avgFirstResponseMinutes: number | null;
  /// Açılıştan `resolvedAt`/`closedAt`a kadar geçen ortalama (dakika) — son 30 gün.
  avgResolutionMinutes: number | null;
  /// Memnuniyet ortalaması (1–5) ve puan sayısı — son 90 gün.
  csatAverage: number | null;
  csatCount: number;
  /// Son 7 günde açılan / kapanan.
  openedLast7Days: number;
  closedLast7Days: number;
}

// ═══════════════════════════════════════════════════════════════════════════
// 🔴 ŞİFRELİ METİNDE ARAMA — kör indeks
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Konu ve gövde şifreli saklandığı için `WHERE body LIKE '%…%'` ARTIK
 * ÇALIŞMIYOR — şifreli metinde alt dize aramanın matematiksel karşılığı yok.
 *
 * Yerine KÖR İNDEKS geldi: her mesajın kelimeleri ayrı bir kasa anahtarıyla
 * HMAC'lenip `support_message_tokens` tablosuna yazılıyor. Arama, sorgunun
 * kelimelerini aynı şekilde HMAC'leyip TAM EŞLEŞME arıyor.
 *
 * ---------------------------------------------------------------------------
 * Kabul edilen bedeller — ikisi de gerçek
 * ---------------------------------------------------------------------------
 *  1. **Arama artık KELİME TAM, alt dize değil.** "veri" araması "verileri"yi
 *     BULMAZ. Önek jetonları (ver, veri, veril…) bunu çözerdi ve indeksi
 *     sözlük saldırısına açardı — 3 harfli bir önek uzayı tükenebilir.
 *     Destek aramasında aranan şey genelde tam bir kelime ("webhook",
 *     "Trendyol", bir hata kodu), bu yüzden bedel kabul edildi.
 *  2. **Frekans analizi.** Aynı kelime her zaman aynı jetonu üretiyor
 *     (deterministik HMAC — aramanın çalışması için ZORUNLU). Veritabanını
 *     ele geçiren biri kelime FREKANSLARINI görebilir ve yaygın kelimeleri
 *     tahmin edebilir. Göremediği şey hangi kelimenin hangi metinde hangi
 *     sırada geçtiği, yani metnin kendisi. Gövdeyi hiç şifrelememekle
 *     karşılaştırıldığında net kazanç.
 *
 * Jeton anahtarı kasa anahtarından TÜRETİLİYOR (HKDF, ayrı `info` etiketi):
 * ikisi ayrı olsun ki jeton anahtarı sızsa bile gövdeler çözülemesin, ama
 * ortamda ikinci bir sır yönetmek gerekmesin.
 *
 * ---------------------------------------------------------------------------
 * SYSTEM satırları indekslenmiyor
 * ---------------------------------------------------------------------------
 * "Ticket closed by the customer." her bilette aynı ve hiç aranmıyor.
 * İndekslemek, `closed`/`ticket` jetonlarını tüm biletlere yayıp frekans
 * analizini kolaylaştırmaktan başka bir şey yapmazdı.
 */

/// Jeton alt sınırı — 3 harften kısa kelimeler ("ve", "bir", "the")
/// indekslenmiyor: aramada ayırt edici değiller ve indeksi şişiriyorlar.
export const SUPPORT_TOKEN_MIN_LENGTH = 3;

/// Tek metinden en fazla bu kadar AYRI jeton. 8.000 karakterlik bir log
/// yapıştırması binlerce benzersiz kelime üretebilir; sınır olmadan tek
/// mesaj indeks tablosuna binlerce satır yazardı.
export const SUPPORT_TOKEN_MAX_PER_TEXT = 200;

/// Sorgudan çıkarılan jeton sayısı üst sınırı. Her jeton bir `INTERSECT`
/// demek; sekiz kelimelik bir aramadan sonrası sorguyu pahalı yapar ve
/// sonucu daraltmaktan başka bir şey yapmaz.
export const SUPPORT_QUERY_MAX_TOKENS = 8;

/**
 * Metni aranabilir kelimelere böler.
 *
 * SAF fonksiyon ve `shared`ta — API (yazma + arama) ile testlerin AYNI
 * bölmeyi kullanması zorunlu: yazarken "webhook'un" kelimesini farklı,
 * ararken farklı bölersek indeks sessizce hiçbir şey bulmaz.
 *
 * 🔴 Türkçe büyük/küçük harf tuzağı: `'I'.toLowerCase()` JavaScript'te `'i'`
 * veriyor ama Türkçe'de `'ı'` olmalı. `toLocaleLowerCase('tr')` de tersini
 * bozuyor — İngilizce "INTEGRATION" `'ıntegratıon'` olur. Çözüm dili
 * tahmin etmemek: önce Unicode normalleştirme (NFKD) + aksan atma, sonra
 * düz `toLowerCase()`. Böylece "İSTEK" ve "istek" aynı jetonu veriyor
 * ("ıstek"/"istek" ayrımı kayboluyor — aramada istenen şey tam olarak bu).
 */
export function tokenizeSupportText(text: string): string[] {
  const seen = new Set<string>();
  const normalized = text
    .normalize('NFKD')
    // Birleştirici aksan işaretleri: "ş" → "s" + U+0327, ikincisi düşüyor.
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

  for (const raw of normalized.split(/[^\p{L}\p{N}]+/u)) {
    if (raw.length < SUPPORT_TOKEN_MIN_LENGTH) continue;
    // Çok uzun "kelime" (base64 yığını, minified JS) aramada kullanılmıyor
    // ve indeksi şişiriyor.
    if (raw.length > 64) continue;
    seen.add(raw);
    if (seen.size >= SUPPORT_TOKEN_MAX_PER_TEXT) break;
  }

  return [...seen];
}

/// Arama kutusundan gelen metni jetonlara böler — yazma tarafıyla AYNI
/// bölme, farklı tavan.
export function tokenizeSupportQuery(query: string): string[] {
  return tokenizeSupportText(query).slice(0, SUPPORT_QUERY_MAX_TOKENS);
}

// ═══════════════════════════════════════════════════════════════════════════
// Kasa anahtarı
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Destek yazışması hangi anahtarla şifrelenir.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 NEDEN `TOKEN_VAULT_KEY`DEN AYRI OLABİLİYOR
 * ═══════════════════════════════════════════════════════════════════════════
 * `TOKEN_VAULT_KEY` OAuth jetonlarını şifreliyor ve o anahtarın ROTASYONU
 * planlanmış bir iş: jeton sızıntısı şüphesinde değiştirilmesi gerekir.
 * Rotasyon sırasında yapılan şey "tüm jetonları eski anahtarla çöz, yenisiyle
 * şifrele" — ve o betik destek yazışmasını TANIMIYOR. Aynı anahtarı
 * paylaşsalardı, jeton rotasyonu tüm destek geçmişini OKUNAMAZ hâle
 * getirirdi ve bu geri alınamaz bir kayıp olurdu.
 *
 * Ayrıca iki verinin ömrü farklı: jeton birkaç ay yaşıyor ve yenisi
 * alınabiliyor, destek yazışması kalıcı bir kayıt.
 *
 * `SUPPORT_VAULT_KEY` verilmemişse `TOKEN_VAULT_KEY`e düşüyor — kurulumu
 * zorlaştırmamak için. Bu SAF fonksiyon çünkü aynı kararı API ve worker
 * ayrı ayrı veriyor (ikisi de env okuyor) ve ayrışmaları sessiz olurdu:
 * worker'ın şifrelediği SYSTEM satırını API çözemezdi.
 */
export function resolveSupportVaultKey(supportKey: string | undefined, tokenKey: string): string {
  return supportKey && supportKey.length > 0 ? supportKey : tokenKey;
}

// ═══════════════════════════════════════════════════════════════════════════
// 🔴 Denetim sözlüğü köprüsü — GEÇİCİ
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Personel işlemlerinin denetim eylemleri.
 *
 * Bunların DOĞRU yeri `admin.ts`teki `ADMIN_AUDIT_ACTION` / `ADMIN_TARGET_TYPE`
 * sözlükleri; o dosya bu turda başka bir yazarın alanında ve tek yazarlı.
 * Sözlükler kapalı birleşim (union) tipi olduğu için yeni bir dize doğrudan
 * `AdminAuditService.record()` imzasına geçmiyor.
 *
 * Bu yüzden dönüşüm TEK YERDE, aşağıdaki iki sabitte yapılıyor — çağrı
 * yerlerine dağılmış `as` ifadeleri yerine. Değerlerin biçimi sözlüğün
 * kuralına uyuyor (`admin.` öneki): `/admin/audit` ekranı satırları
 * `startsWith('admin.')` ile ayırıyor, yani köprü olmadan da doğru tarafta
 * görünüyorlar.
 *
 * 🔧 `admin.ts` açıldığında yapılacak: üç eylem ve `support_ticket` hedef tipi
 * oradaki sözlüklere taşınır, buradaki iki sabit silinir, çağrı yerleri
 * `ADMIN_AUDIT_ACTION.SUPPORT_*` yazar. Davranış değişmez.
 */
export const SUPPORT_ADMIN_AUDIT_ACTION = {
  TICKET_REPLIED: 'admin.support.ticket_replied',
  TICKET_STATUS_CHANGED: 'admin.support.ticket_status_changed',
  TICKET_PRIORITY_CHANGED: 'admin.support.ticket_priority_changed',
  /// Atama ve bırakma TEK eylem adı taşıyor: `before`/`after` hangisi
  /// olduğunu zaten söylüyor ve iki ayrı ad, "bu bilet kim tarafından
  /// devredildi" sorusunu iki filtreye bölerdi.
  TICKET_ASSIGNED: 'admin.support.ticket_assigned',
} as const;

/// Köprünün kendisi — `AdminAuditAction` birleşimine tek dönüşüm noktası.
export const supportAdminAuditAction = SUPPORT_ADMIN_AUDIT_ACTION as unknown as Record<
  keyof typeof SUPPORT_ADMIN_AUDIT_ACTION,
  AdminAuditAction
>;

export const SUPPORT_ADMIN_TARGET_TYPE = 'support_ticket' as unknown as AdminTargetType;

// ═══════════════════════════════════════════════════════════════════════════
// Yardım merkezi — uç sözleşmesi
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Makale METİNLERİ bu dosyada DEĞİL.
 *
 * `help-center.ts` on makalenin TR + EN tam gövdesini taşıyor (~600 satır) ve
 * kök barrel'dan export EDİLMİYOR — `mail.ts`, `template-seed.ts` ve
 * `demo-data.ts` ile aynı gerekçe. Burada yalnız uç sözleşmesi var; arayüz
 * makaleleri sunucudan çekiyor, bundle'ına gömmüyor.
 */
export const HELP_CATEGORIES = [
  'getting-started',
  'connections',
  'automation',
  'billing',
  'notifications',
  'team',
  'data',
  'support',
] as const;
export type HelpCategory = (typeof HELP_CATEGORIES)[number];

export const helpArticleListQuerySchema = z.object({
  category: z.enum(HELP_CATEGORIES).optional(),
  /**
   * Serbest arama — başlık, özet ve gövdede geçiyor mu.
   *
   * `common.ts`teki `searchParam` KULLANILMADI: o `.trim().min(1)` ile boş
   * dizeyi reddediyor ve arama kutusu boşaltıldığında istemci `?q=` gönderiyor.
   * Burada boş `q` "arama yok" demek, 400 değil.
   */
  q: z
    .string()
    .max(120)
    .optional()
    .transform((v) => {
      const trimmed = v?.trim() ?? '';
      return trimmed.length > 0 ? trimmed : undefined;
    }),
  /// Verilmezse kullanıcının `User.locale` değeri kullanılır.
  locale: localeSchema.optional(),
});
export type HelpArticleListQuery = z.infer<typeof helpArticleListQuerySchema>;

export const helpArticleDetailQuerySchema = z.object({
  locale: localeSchema.optional(),
});
export type HelpArticleDetailQuery = z.infer<typeof helpArticleDetailQuerySchema>;

export interface HelpArticleSummary {
  slug: string;
  category: HelpCategory;
  title: string;
  summary: string;
  /// Makalenin anlattığı panel ekranı (`/dashboard/...`). `null` = ekrana
  /// bağlı olmayan kavramsal makale. Ham URL değil ROTA saklanıyor; arayüz
  /// dili ve ön eki kendisi ekliyor.
  route: string | null;
}

export interface HelpArticleDetail extends HelpArticleSummary {
  paragraphs: string[];
  /**
   * Aynı kategorideki diğer makaleler (en fazla 3).
   *
   * Sunucuda hesaplanıyor çünkü liste ucu ile detay ucu ayrı: arayüz "ilgili
   * makaleler" için tüm kataloğu ikinci kez çekmek zorunda kalmasın.
   */
  related: HelpArticleSummary[];
}

export interface HelpCategoryCount {
  key: HelpCategory;
  count: number;
}

export interface HelpArticleListResponse {
  articles: HelpArticleSummary[];
  /// Kategori sekmelerinin kaynağı. Sayılar FİLTRESİZ katalogdan geliyor —
  /// filtre uygulandığında sekmelerin hepsinin sıfıra düşmesi, kullanıcının
  /// filtreyi kaldıracak yeri bulamaması demekti.
  categories: HelpCategoryCount[];
  /// İstenen dil (`?locale=` ya da `User.locale`). Katalog `tr`/`en` el
  /// yazımı; diğer on bir dilin metni çeviri sözlüğünden geliyor
  /// (`@mentorivo/shared/text-dictionary`) ve sözlükte olmayan metin
  /// İngilizce kalıyor.
  locale: Locale;
  total: number;
}

// ═══════════════════════════════════════════════════════════════════════════
// Hatalar
// ═══════════════════════════════════════════════════════════════════════════

export const SupportErrorCode = {
  /// Bilet yok ya da başka bir kiracıya ait — ikisi de aynı yanıt (404).
  TICKET_NOT_FOUND: 'TICKET_NOT_FOUND',
  /**
   * Bilet `CLOSED` — yazılamaz. 409, 400 değil: istek geçerli, durum çakışıyor.
   *
   * `RESOLVED` bu hatayı VERMEZ; kullanıcının "aslında çözülmemiş" demesi
   * meşru bir hareket ve bilet `WAITING_STAFF`a geri döner. Kapalı bilet ise
   * yeniden açılmıyor (şema kararı) — arayüz yeni bilet açtırmalı.
   */
  TICKET_CLOSED: 'TICKET_CLOSED',
  /// Açık bilet tavanı doldu. `details.limit` ve `details.open` dolu. HTTP 409.
  TOO_MANY_OPEN_TICKETS: 'TOO_MANY_OPEN_TICKETS',
  ARTICLE_NOT_FOUND: 'ARTICLE_NOT_FOUND',

  /// Gövde de ek de boş. HTTP 400 — mesajın bir içeriği olmak zorunda.
  EMPTY_MESSAGE: 'EMPTY_MESSAGE',
  /**
   * Ek dosya reddedildi: tür allowlist'te değil, boyut tavanı aşıldı ya da
   * anahtar bu kiracıya ait değil. Üçü TEK kod çünkü istemcinin yapacağı şey
   * aynı (dosyayı değiştir) ve ayrıntı `details` içinde.
   *
   * 🔴 "Anahtar bu kiracıya ait değil" ayrı bir 403 DEĞİL: 403, denenen
   * anahtarın GEÇERLİ olduğunu doğrulardı. 400 hiçbir şey söylemiyor.
   */
  ATTACHMENT_REJECTED: 'ATTACHMENT_REJECTED',
  /// Depolama yapılandırılmamış (`S3_BUCKET` boş). HTTP 503 — istemcinin
  /// hatası değil, sunucunun eksiği; tekrar denemek anlamlı.
  STORAGE_NOT_CONFIGURED: 'STORAGE_NOT_CONFIGURED',
  /// Bilet henüz puanlanabilir durumda değil (`RESOLVED`/`CLOSED` değil). 409.
  TICKET_NOT_RATABLE: 'TICKET_NOT_RATABLE',
  /// Zaten puanlanmış. 409 — puanlama tek seferlik (gerekçe şemada).
  ALREADY_RATED: 'ALREADY_RATED',
  /// Atanmak istenen kişi platform personeli değil. HTTP 400.
  ASSIGNEE_NOT_STAFF: 'ASSIGNEE_NOT_STAFF',
} as const;
export type SupportErrorCode = (typeof SupportErrorCode)[keyof typeof SupportErrorCode];
